import React, { useEffect, useRef } from 'react';

// lib files
import { Theme } from "@/lib/types/api/multi-livestream";

// components
import Button from '@/components/Button/Button';

// svg's
import CloseIcon from '@/public/svg/close.svg';

import styles from '@/components/MultiLivestream/Schedule/ListingModal.module.scss';

interface MultiLivestreamListingModalProps {
  channelCid: string;
  channelIsSelected: boolean;
  channelLogo: string;
  channelTitle: string;
  closeModal: () => void;
  episodeDescription: string;
  episodeTitle: string;
  formattedDuration: string;
  isAiringNow: boolean;
  listingDuration: number;
  listingProgress: number;
  openModal: boolean;
  setUserSelectedChannel: (channelCid: string) => void;
  showTitle: string;
  timeRemaining: string;
  theme: Theme;
}

const MultiLivestreamListingModal = (
  props: MultiLivestreamListingModalProps
) => {
  const {
    channelCid,
    channelIsSelected,
    channelLogo,
    channelTitle,
    closeModal,
    episodeDescription,
    episodeTitle,
    formattedDuration,
    isAiringNow,
    listingDuration,
    listingProgress,
    openModal,
    setUserSelectedChannel,
    showTitle,
    timeRemaining,
    theme,
  } = props;

  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (openModal) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }
  }, [openModal]);

  const handleModalClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    const target = e.target as HTMLElement;
    if (target === ref.current) {
      closeModal();
    }
  };

  return (
    <dialog
      ref={ref}
      onCancel={closeModal}
      onClick={handleModalClick}
      className={styles.listing_modal}
    >
      {/* this inner div is necessary so we can listen for mouse clicks outside the dialog and close it in with the click event listener */}
      <div className={styles.listing_modal__inner}>
        {channelLogo ? (
          <img
            src={channelLogo}
            className={styles.listing_modal__logo}
            alt={channelTitle}
          />
        ) : (
          <span className={styles.listing_modal__channel_text}>{channelTitle}</span>
        )}
        <h2 className={styles.listing_modal__show_title}>{showTitle}</h2>
        {episodeTitle && (
          <h3 className={styles.listing_modal__episode_title}>{episodeTitle}</h3>
        )}
        {episodeDescription && (
          <p className={styles.listing_modal__episode_description}>
            {episodeDescription}
          </p>
        )}
        {isAiringNow && (
          <>
            <div className={styles.listing_modal__progress}>
              <progress
                className={styles.listing_modal__progress_bar}
                max={listingDuration}
                value={listingProgress}
              />
              <span className={styles.listing_modal__time_remaining}>
                {timeRemaining}
              </span>
            </div>
            {!channelIsSelected && (
              <Button
                onClick={() => {
                  setUserSelectedChannel(channelCid);
                  closeModal();
                }}
                iconBefore='play'
                style={ theme === 'light' ? 'blue' : 'white'}
              >
                Watch Now
              </Button>
            )}
          </>
        )}
        {!isAiringNow && (
          <p className={styles.listing_modal__duration}>{formattedDuration}</p>
        )}
        <button onClick={closeModal} className={styles.listing_modal__close_btn}>
          <CloseIcon />
          <span className="visuallyhidden">Close</span>
        </button>
      </div>
    </dialog>
  );
};

export default MultiLivestreamListingModal;
